import {createSelector} from 'reselect';
import isempty from 'lodash/isEmpty';

import {convertToLocaleTimeString} from '../utils/timeUtils';
import guardedSelector from '../utils/guardedSelector';
import {getListingHouseRules, getLegacyLodgingPolicy} from './listing';
import {getLocaleJS} from './system';
import {getTranslations} from './store';

const getBinaryHouseRules = createSelector(
    [getListingHouseRules],
    (listingHouseRules = {}) => {
        const {events, smoking, pets, children} = listingHouseRules;
        return [events, smoking, pets, children].filter((rule) => rule);
    }
);

const getValueHouseRules = createSelector(
    [getListingHouseRules],
    (listingHouseRules = {}) => {
        const {maxOccupancy, minimumAge} = listingHouseRules;
        const valueRules = [];

        if (minimumAge) {
            valueRules.push({
                value: minimumAge.minimumAge,
                label: minimumAge.label,
                note: minimumAge.note
            });
        }

        if (maxOccupancy) {
            let value = maxOccupancy.guests;
            if (value && maxOccupancy.adults) {
                value += ` ${maxOccupancy.maxAdultsLabel}`;
            }

            valueRules.push({
                value,
                label: maxOccupancy.label,
                note: maxOccupancy.note
            });
        }

        return valueRules;
    }
);

const getCustomHouseRules = createSelector(
    [getListingHouseRules],
    (listingHouseRules = {}) => {
        return listingHouseRules.customRules || [];
    }
);

const getHouseRules = guardedSelector(
    'getHouseRules',
    createSelector(
        [
            getBinaryHouseRules,
            getValueHouseRules,
            getCustomHouseRules,
            getListingHouseRules,
            getLegacyLodgingPolicy,
            getLocaleJS,
            getTranslations
        ],
        (
            binaryRules,
            valueRules,
            customRules,
            listingHouseRules,
            legacyLodgingPolicy,
            locale,
            translations
        ) => {
            // we only want check in/out from legacy rules
            const safeHouseRules =
                (!isempty(listingHouseRules) && listingHouseRules) || legacyLodgingPolicy || {};
            const {checkInTime, checkOutTime} = safeHouseRules;
            const showHouseRulesSection = checkInTime && checkOutTime;

            // if property is using legacy, unstructured house rules we can short circuit
            if (isempty(listingHouseRules)) {
                return {
                    showHouseRulesSection: !!showHouseRulesSection,
                    displayHouseRules: false,
                    checkIn: convertToLocaleTimeString(checkInTime, locale),
                    checkOut: convertToLocaleTimeString(checkOutTime, locale),
                    labels: translations
                };
            }

            return {
                showHouseRulesSection: true,
                displayHouseRules: !!(binaryRules.length + valueRules.length + customRules.length),
                checkIn: convertToLocaleTimeString(checkInTime, locale),
                checkOut: convertToLocaleTimeString(checkOutTime, locale),
                binaryRules,
                valueRules,
                customRules,
                listingHouseRules,
                labels: translations
            };
        }
    )
);
/* eslint-enable complexity */

export default getHouseRules;
export {getHouseRules, getBinaryHouseRules, getValueHouseRules, getCustomHouseRules};
